import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FormDefinition, FormUpdateRequest } from "@eduviaos/shared/types";
import { api } from "@/lib/api";

/**
 * SA-side cross-tenant form hooks (Phase B2.2).
 *
 * Wrap the new BE endpoints:
 *   GET   /super-admin/tenants/{slug}/forms/{form_slug}
 *   PATCH /super-admin/tenants/{slug}/forms/{form_slug}
 *
 * These power the "Edit fields" entry-point on the tenant detail
 * Forms tab. The SA-level `/forms` editor is gone (it was orphaned
 * from real data because every form is tenant-scoped); this is the
 * canonical surface for SA-side form editing.
 */

const tenantFormKey = {
  detail: (tenantSlug: string, formSlug: string) =>
    ["super-admin", "tenant-form", tenantSlug, formSlug] as const,
};

export function useTenantForm(tenantSlug: string, formSlug: string) {
  return useQuery<FormDefinition>({
    queryKey: tenantFormKey.detail(tenantSlug, formSlug),
    queryFn: () => api.get<FormDefinition>(`/super-admin/tenants/${tenantSlug}/forms/${formSlug}`),
    enabled: Boolean(tenantSlug) && Boolean(formSlug),
    staleTime: 15_000,
  });
}

export function useUpdateTenantForm(tenantSlug: string, formSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: FormUpdateRequest) =>
      api.patch<FormDefinition>(`/super-admin/tenants/${tenantSlug}/forms/${formSlug}`, req),
    onSuccess: (form) => {
      // Hydrate the detail cache + bust the tenant-scoped list so the
      // "Forms" tab reflects new field counts and updated_at.
      qc.setQueryData(tenantFormKey.detail(tenantSlug, formSlug), form);
      qc.invalidateQueries({
        queryKey: ["super-admin", "tenant-forms", tenantSlug],
      });
    },
  });
}
